import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import { MenuProvider } from './context/MenuContext';
import { CafeteriaSelectionProvider } from './context/CafeteriaSelectionContext';
import { useUserPermissions } from './hooks/useUserPermissions';
import LoginPage from './pages/auth/LoginPage';
import TokenExpiredPage from './pages/auth/TokenExpiredPage';
import DashboardPage from './pages/cafeteria/dashboard/DashboardPage';
import PlaceInfoPage from './pages/cafeteria/places/PlaceInfoPage';
import ProductsPage from './pages/cafeteria/products/ProductsPage';
import ReviewsPage from './pages/cafeteria/reviews/ReviewsPage';
import BusinessHoursPage from './pages/cafeteria/hours/BusinessHoursPage';
import EventsListPage from './pages/cafeteria/events/EventsListPage';
import EventFormPage from './pages/cafeteria/events/EventFormPage';
import SuperAdminEventsListPage from './pages/super-admin/events/EventsListPage';
import SuperAdminEventFormPage from './pages/super-admin/events/EventFormPage';
import SettingsPage from './pages/settings/SettingsPage';
import LegalPage from './pages/legal/LegalPage';
import UsersPage from './pages/super-admin/users/UsersPage';
import BusinessUsersPage from './pages/super-admin/business-users/BusinessUsersPage';
import BusinessUserCreatePage from './pages/super-admin/business-users/BusinessUserCreatePage';
import BusinessUserEditPage from './pages/super-admin/business-users/BusinessUserEditPage';
import PlacesPage from './pages/cafeteria/places/PlacesPage';
import PlaceCreatePage from './pages/cafeteria/places/PlaceCreatePage';
import PlaceEditPage from './pages/cafeteria/places/PlaceEditPage';
import CategoriesPage from './pages/super-admin/categories/CategoriesPage';
import CoffeeProfileLevelsPage from './pages/super-admin/coffee-profile-levels/CoffeeProfileLevelsPage';
import CharacteristicsPage from './pages/super-admin/characteristics/CharacteristicsPage';
import CouponsPage from './pages/super-admin/coupons/CouponsPage';
import CoffeeInReviewsPage from './pages/super-admin/coffeein-reviews/CoffeeInReviewsPage';
import CoffeeInReviewEditPage from './pages/super-admin/coffeein-reviews/CoffeeInReviewEditPage';
import TorrefacoesPage from './pages/super-admin/torrefacoes/TorrefacoesPage';
import TorrefacaoCreatePage from './pages/super-admin/torrefacoes/TorrefacaoCreatePage';
import TorrefacaoEditPage from './pages/super-admin/torrefacoes/TorrefacaoEditPage';
import TorrefacaoViewPage from './pages/super-admin/torrefacoes/TorrefacaoViewPage';
import RoutesPage from './pages/super-admin/routes/RoutesPage';
import RouteFormPage from './pages/super-admin/routes/RouteFormPage';
import PostsListPage from './pages/posts/PostsListPage';
import PostFormPage from './pages/posts/PostFormPage';
import PostViewPage from './pages/posts/PostViewPage';
import SendNotificationPage from './pages/super-admin/notifications/SendNotificationPage';
import Layout from './components/layout/Layout';
import LoadingSpinner from './components/ui/LoadingSpinner';
import PermissionProtectedRoute from './components/auth/ProtectedRoute';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
};

// App Routes Component
const AppRoutes = () => {
  const { isAuthenticated } = useAuth();
  const { canManageSystemUsers } = useUserPermissions();

  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={
          isAuthenticated ? <Navigate to={canManageSystemUsers ? "/places" : "/dashboard"} replace /> : <LoginPage />
        } 
      />
      <Route 
        path="/token-expired" 
        element={<TokenExpiredPage />} 
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to={canManageSystemUsers ? "/places" : "/dashboard"} replace />} />
        
        {/* Rotas específicas de cafeteria - escondidas para superadmins */}
        <Route 
          path="dashboard" 
          element={
            canManageSystemUsers ? (
              <Navigate to="/business-users" replace />
            ) : (
              <DashboardPage />
            )
          } 
        />
        <Route 
          path="place-info" 
          element={
            canManageSystemUsers ? (
              <Navigate to="/business-users" replace />
            ) : (
              <PlaceInfoPage />
            )
          } 
        />
        <Route 
          path="products" 
          element={
            canManageSystemUsers ? (
              <Navigate to="/business-users" replace />
            ) : (
              <ProductsPage />
            )
          } 
        />
        <Route 
          path="reviews" 
          element={
            canManageSystemUsers ? (
              <Navigate to="/business-users" replace />
            ) : (
              <ReviewsPage />
            )
          } 
        />
        <Route 
          path="hours" 
          element={
            canManageSystemUsers ? (
              <Navigate to="/business-users" replace />
            ) : (
              <BusinessHoursPage />
            )
          } 
        />
        <Route 
          path="cafeteria/events" 
          element={
            canManageSystemUsers ? (
              <Navigate to="/business-users" replace />
            ) : (
              <EventsListPage />
            )
          } 
        />
        <Route 
          path="cafeteria/events/new" 
          element={
            canManageSystemUsers ? (
              <Navigate to="/business-users" replace />
            ) : (
              <EventFormPage />
            )
          } 
        />
        <Route 
          path="cafeteria/events/:id" 
          element={
            canManageSystemUsers ? (
              <Navigate to="/business-users" replace />
            ) : (
              <EventFormPage />
            )
          } 
        />
        <Route 
          path="cafeteria/events/:id/edit" 
          element={
            canManageSystemUsers ? (
              <Navigate to="/business-users" replace />
            ) : (
              <EventFormPage />
            )
          } 
        />
        
        {/* Rotas de Posts - disponíveis para PlaceAdmin e SuperAdmin */}
        <Route 
          path="posts" 
          element={<PostsListPage />} 
        />
        <Route 
          path="posts/new" 
          element={<PostFormPage />} 
        />
        <Route 
          path="posts/:id" 
          element={<PostViewPage />} 
        />
        <Route 
          path="posts/:id/edit" 
          element={<PostFormPage />} 
        />
        
        {/* Rotas sempre disponíveis */}
        <Route path="settings" element={<SettingsPage />} />
        
        {/* Rotas de superadmin */}
        <Route 
          path="legal" 
          element={
            <PermissionProtectedRoute requiredPermission="canManageLegal">
              <LegalPage />
            </PermissionProtectedRoute>
          } 
        />
        <Route
          path="users"
          element={
            <PermissionProtectedRoute requiredPermission="canManageSystemUsers">
              <UsersPage />
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="business-users"
          element={
            <PermissionProtectedRoute requiredPermission="canManageSystemUsers">
              <BusinessUsersPage />
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="business-users/new"
          element={
            <PermissionProtectedRoute requiredPermission="canManageSystemUsers">
              <BusinessUserCreatePage />
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="business-users/:id/edit"
          element={
            <PermissionProtectedRoute requiredPermission="canManageSystemUsers">
              <BusinessUserEditPage />
            </PermissionProtectedRoute>
          }
        />
        <Route 
          path="places" 
          element={
            <PermissionProtectedRoute requiredPermission="canManagePlaces">
              <PlacesPage />
            </PermissionProtectedRoute>
          } 
        />
        <Route 
          path="places/new" 
          element={
            <PermissionProtectedRoute requiredPermission="canManagePlaces">
              <PlaceCreatePage />
            </PermissionProtectedRoute>
          } 
        />
        <Route 
          path="places/:placeId/edit" 
          element={
            <PermissionProtectedRoute requiredPermission="canManagePlaces">
              <PlaceEditPage />
            </PermissionProtectedRoute>
          } 
        />
        
        {/* Rotas de Torrefações */}
        <Route 
          path="torrefacoes" 
          element={
            <PermissionProtectedRoute requiredPermission="canManagePlaces">
              <TorrefacoesPage />
            </PermissionProtectedRoute>
          } 
        />
        <Route 
          path="torrefacoes/create" 
          element={
            <PermissionProtectedRoute requiredPermission="canManagePlaces">
              <TorrefacaoCreatePage />
            </PermissionProtectedRoute>
          } 
        />
        <Route 
          path="torrefacoes/:id" 
          element={
            <PermissionProtectedRoute requiredPermission="canManagePlaces">
              <TorrefacaoViewPage />
            </PermissionProtectedRoute>
          } 
        />
        <Route 
          path="torrefacoes/:id/edit" 
          element={
            <PermissionProtectedRoute requiredPermission="canManagePlaces">
              <TorrefacaoEditPage />
            </PermissionProtectedRoute>
          } 
        />
        <Route 
          path="routes" 
          element={
            <PermissionProtectedRoute requiredPermission="canManageSystemUsers">
              <RoutesPage />
            </PermissionProtectedRoute>
          } 
        />
        <Route 
          path="routes/new" 
          element={
            <PermissionProtectedRoute requiredPermission="canManageSystemUsers">
              <RouteFormPage />
            </PermissionProtectedRoute>
          } 
        />
        <Route 
          path="routes/:id/edit" 
          element={
            <PermissionProtectedRoute requiredPermission="canManageSystemUsers">
              <RouteFormPage />
            </PermissionProtectedRoute>
          } 
        />
        
        <Route 
          path="events" 
          element={
            <PermissionProtectedRoute requiredPermission="canManageSystemUsers">
              <SuperAdminEventsListPage />
            </PermissionProtectedRoute>
          } 
        />
        <Route 
          path="events/new" 
          element={
            <PermissionProtectedRoute requiredPermission="canManageSystemUsers">
              <SuperAdminEventFormPage />
            </PermissionProtectedRoute>
          } 
        />
        <Route 
          path="events/:id" 
          element={
            <PermissionProtectedRoute requiredPermission="canManageSystemUsers">
              <SuperAdminEventFormPage />
            </PermissionProtectedRoute>
          } 
        />
        <Route 
          path="events/:id/edit" 
          element={
            <PermissionProtectedRoute requiredPermission="canManageSystemUsers">
              <SuperAdminEventFormPage />
            </PermissionProtectedRoute>
          } 
        />
        <Route
          path="categories"
          element={
            <PermissionProtectedRoute requiredPermission="canManageSystemUsers">
              <CategoriesPage />
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="coffee-profile-levels"
          element={
            <PermissionProtectedRoute requiredPermission="canManageSystemUsers">
              <CoffeeProfileLevelsPage />
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="characteristics"
          element={
            <PermissionProtectedRoute requiredPermission="canManageSystemUsers">
              <CharacteristicsPage />
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="coupons"
          element={
            <PermissionProtectedRoute requiredPermission="canManageSystemUsers">
              <CouponsPage />
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="super-admin/coffeein-reviews"
          element={
            <PermissionProtectedRoute requiredPermission="canManageSystemUsers">
              <CoffeeInReviewsPage />
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="super-admin/coffeein-reviews/:id"
          element={
            <PermissionProtectedRoute requiredPermission="canManageSystemUsers">
              <CoffeeInReviewEditPage />
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="notifications"
          element={
            <PermissionProtectedRoute requiredPermission="canManageSystemUsers">
              <SendNotificationPage />
            </PermissionProtectedRoute>
          }
        />
      </Route>

      {/* Fallback Route */}
      <Route path="*" element={<Navigate to={canManageSystemUsers ? "/system-users" : "/dashboard"} replace />} />
    </Routes>
  );
};

// Main App Component
const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CafeteriaSelectionProvider>
          <MenuProvider>
            <Router>
              <AppRoutes />
            </Router>
          </MenuProvider>
        </CafeteriaSelectionProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App; 
